import { toastCopy } from "@/lib/journal/labels";
import type { Customer360, JournalEvent, SessionResponse, TicketRecord } from "@/lib/types";

async function parse<T>(response: Response): Promise<T> {
  const data = (await response.json().catch(() => ({}))) as { error?: string; message?: string };
  if (!response.ok) {
    const message = data.message || data.error || "Request failed";
    const error = new Error(message);
    (error as Error & { status: number }).status = response.status;
    throw error;
  }
  return data as T;
}

export type CreditResult = { status: "posted" | "pendingApproval"; amount: number; reason: string; taskId: string };
export type CreditTask = {
  id: string;
  customerId: string;
  amount: number;
  reason: string;
  note: string;
  requesterLimit: number;
  requestedBy: { userId: string; role: string };
  requestedAt: string;
  status: "pending" | "posted" | "denied";
};
export type RuntimeConfig = {
  service: string;
  gcpProject: string;
  cloudRunService: string;
  cloudRunRevision: string;
  bffUpstream: "mock" | "real" | "notImplemented";
};

export const api = {
  config: () => fetch("/config.json").then((r) => parse<RuntimeConfig>(r)),
  session: () => fetch("/api/session").then((r) => parse<SessionResponse>(r)),
  setRole: (role: string) =>
    fetch("/api/session", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ role }),
    }).then((r) => parse<SessionResponse>(r)),
  verifyChallenge: (customerId: string) =>
    fetch("/api/session/verify/challenge", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ method: "otp", customerId }),
    }).then((r) => parse<{ sent: true; hint: string }>(r)),
  verify: (input: { method: string; customerId: string; code?: string; pin?: string }) =>
    fetch("/api/session/verify", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
    }).then((r) => parse<SessionResponse>(r)),
  journal: () => fetch("/api/journal").then((r) => parse<{ events: JournalEvent[] }>(r)),
  writeJournal: (action: string, customerId: string | undefined, detail: Record<string, string | number | boolean | null> = {}) =>
    fetch("/api/journal", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action, customerId, detail }),
    }).then((r) => parse<JournalEvent>(r)),
  search: (q: string) =>
    fetch("/api/search", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ q }),
    }).then((r) => parse<{ results: { id: string; name: string; kind: string; accountNumber: string }[] }>(r)),
  customer360: (id: string) => fetch(`/api/customer-360/${id}`).then((r) => parse<Customer360>(r)),
  usage: (id: string) => fetch(`/api/customers/${id}/usage`).then((r) => parse<{ lines: Customer360["usage"] }>(r)),
  createTicket: (id: string, body: { summary: string; priority: TicketRecord["priority"]; category: string }) =>
    fetch(`/api/customers/${id}/tickets`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }).then((r) => parse<TicketRecord>(r)),
  payLink: (customerId: string) =>
    fetch("/api/actions/pay-link", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ customerId }),
    }).then((r) => parse<{ ok: boolean; linkId: string }>(r)),
  ivrPay: (customerId: string) =>
    fetch("/api/actions/ivr-pay", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ customerId }),
    }).then((r) => parse<{ ok: boolean; recordingPaused: boolean }>(r)),
  credit: (body: { customerId: string; amount: number; reason: string; note: string }) =>
    fetch("/api/actions/credit", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }).then((r) => parse<CreditResult>(r)),
  approvals: () => fetch("/api/approvals").then((r) => parse<{ tasks: CreditTask[] }>(r)),
  decideApproval: (id: string, decision: "approve" | "deny") =>
    fetch(`/api/approvals/${id}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ decision }),
    }).then((r) => parse<{ status: string; taskId: string; amount: number }>(r)),
  consent: (id: string) =>
    fetch(`/api/customers/${id}/consent`).then((r) =>
      parse<{ versions: { effective: string; channel: string; serviceDataForOffers: string }[] }>(r),
    ),
  appointments: (id: string) =>
    fetch(`/api/customers/${id}/appointments`).then((r) =>
      parse<{ appointments: { id: string; window: string; status: string; work: string }[] }>(r),
    ),
  offerAppointments: (id: string) =>
    fetch(`/api/customers/${id}/appointments/offer`, { method: "POST" }).then((r) =>
      parse<{ appointments: { id: string; window: string; status: string; work: string }[] }>(r),
    ),
  diagnostics: (customerId: string) =>
    fetch("/api/actions/diagnostics", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ customerId }),
    }).then((r) => parse<{ result: string; status: string }>(r)),
  deviceAction: (customerId: string, summary: string) =>
    fetch("/api/actions/device", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ customerId, summary }),
    }).then((r) => parse<{ ok: boolean }>(r)),
  adminMatrix: () => fetch("/api/admin/matrix").then((r) => parse<{
    roles: unknown[];
    users: { name: string; role: string; status: string; mfa: boolean; lastSignIn: string }[];
    capabilities: { name: string; values: string[] }[];
  }>(r)),
};

export { toastCopy };
